import type Highcharts from 'highcharts';

import type { ChartProps, ChartType } from './catalogConstants';

const HIGHCHARTS_TYPE: Record<ChartType, string> = {
  bar: 'column',
  horizontalBar: 'bar',
  line: 'line',
  spline: 'spline',
  area: 'area',
  pie: 'pie',
  donut: 'pie',
};

const CIRCULAR: ReadonlySet<ChartType> = new Set<ChartType>(['pie', 'donut']);

const DONUT_INNER_SIZE = '60%';

const circularSeries = (
  props: ChartProps,
  type: string
): Highcharts.SeriesOptionsType[] => {
  const [first] = props.series;

  return [
    {
      type: 'pie',
      name: first?.name ?? 'Series',
      ...(type === 'pie' && props.chartType === 'donut'
        ? { innerSize: DONUT_INNER_SIZE }
        : {}),
      data: (first?.data ?? [])
        .map((y, index) => ({
          name: props.categories?.[index] ?? `#${index + 1}`,
          y,
        }))
        .filter(
          (point): point is { name: string; y: number } => point.y !== null
        ),
    },
  ];
};

const cartesianSeries = (
  props: ChartProps,
  type: string
): Highcharts.SeriesOptionsType[] =>
  props.series.map((series) => {
    // TS can't narrow SeriesOptionsType from a runtime `type`; valid by construction.
    const option = { type, name: series.name, data: series.data };

    return option as Highcharts.SeriesOptionsType;
  });

/**
 * Titles and labels are AGENT-SUPPLIED. Never set `useHTML` here — it routes text
 * through innerHTML, making a model-authored label an injection vector. A sibling
 * hook does set it (`useGetPieChartDefaultOptions.ts`); the test pins that we do not.
 */
export const buildChartOptions = (props: ChartProps): Highcharts.Options => {
  const type = HIGHCHARTS_TYPE[props.chartType];
  const isCircular = CIRCULAR.has(props.chartType);

  // Meaningless on a ring, so ignored rather than half-applied.
  const stacking =
    props.isStacked === true && !isCircular
      ? { series: { stacking: 'normal' as const } }
      : undefined;

  return {
    chart: { type },
    title: { text: props.title ?? '' },
    // A ring takes its labels from the slice names, not an axis.
    ...(props.categories && !isCircular
      ? { xAxis: { categories: props.categories } }
      : {}),
    ...(stacking ? { plotOptions: stacking } : {}),
    series: isCircular
      ? circularSeries(props, type)
      : cartesianSeries(props, type),
  };
};
